import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  App,
  Alert,
  Statistic,
  Row,
  Col,
  Switch,
  Popconfirm,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined, GiftOutlined, StopOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { useAgencyAuthStore } from '../../stores/agencyAuthStore';
import { PageHeader, PageContainer } from '../../components/common';
import type { ColumnsType } from 'antd/es/table';

const { Text, Paragraph } = Typography;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeDateText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (
    value &&
    typeof value === 'object' &&
    'toISOString' in value &&
    typeof value.toISOString === 'function'
  ) {
    return value.toISOString();
  }

  return String(value ?? '');
};

export function AgencyCouponsPage() {
  const { message } = App.useApp();
  const { token } = useAgencyAuthStore();
  const utils = trpc.useUtils();

  if (!token) {
    return <Alert message="認証が必要です" type="error" />;
  }

  // 利用可能なグローバルクーポン一覧取得（発行状況含む）
  const { data: couponsData, isLoading } = trpc.globalAgencyCoupon.listAvailable.useQuery(
    undefined,
    { enabled: !!token }
  );

  // 統計情報取得
  const { data: stats } = trpc.globalAgencyCoupon.getMyStatistics.useQuery(undefined, {
    enabled: !!token,
  });

  // プロモーションコード発行
  const issueMutation = trpc.globalAgencyCoupon.issuePromotionCode.useMutation({
    onSuccess: () => {
      message.success('プロモーションコードを発行しました');
      utils.globalAgencyCoupon.listAvailable.invalidate();
      utils.globalAgencyCoupon.getMyStatistics.invalidate();
    },
    onError: (error) => {
      message.error(error.message || 'プロモーションコードの発行に失敗しました');
    },
  });

  // プロモーションコードステータス切り替え
  const toggleStatusMutation = trpc.globalAgencyCoupon.togglePromotionCodeStatus.useMutation({
    onSuccess: (_, variables) => {
      const input = isRecord(variables) ? variables : null;
      const isActive = typeof input?.isActive === 'boolean' ? input.isActive : false;
      message.success(`プロモーションコードを${isActive ? '有効' : '無効'}にしました`);
      utils.globalAgencyCoupon.listAvailable.invalidate();
    },
    onError: (error) => {
      message.error(error.message || 'ステータスの更新に失敗しました');
    },
  });

  const handleIssue = (globalCouponId: string) => {
    issueMutation.mutate({
      globalCouponId,
    });
  };

  const handleToggleStatus = (issuanceId: string, isActive: boolean) => {
    toggleStatusMutation.mutate({
      issuanceId,
      isActive,
    });
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success('プロモーションコードをコピーしました');
  };

  type CouponRow = {
    id: string;
    stripeCouponId: string;
    stripeCouponName: string | null;
    displayName: string | null;
    discountType: string;
    discountValue: string;
    currency: string | null;
    duration: string;
    durationInMonths: number | null;
    issueLimit: string;
    maxIssuesPerAgency: number | null;
    canIssue: boolean;
    canIssueReason?: string;
    myIssuedCount: number;
    myIssuances: Array<{
      id: string;
      promotionCode: string;
      issuedAt: string;
      isActive: boolean;
      timesRedeemed: number;
      maxRedemptions: number;
    }>;
  };

  const pendingIssuanceId =
    isRecord(toggleStatusMutation.variables) && typeof toggleStatusMutation.variables.issuanceId === 'string'
      ? toggleStatusMutation.variables.issuanceId
      : null;

  const columns: ColumnsType<CouponRow> = [
    {
      title: 'クーポン名',
      key: 'couponName',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.displayName || record.stripeCouponName || '-'}</Text>
          {record.displayName && record.stripeCouponName && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.stripeCouponName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '割引内容',
      key: 'discount',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.discountType === 'percentage'
              ? `${record.discountValue}% OFF`
              : `¥${Math.floor(Number(record.discountValue))} OFF`}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.duration === 'once'
              ? '1回のみ'
              : record.duration === 'forever'
                ? '永続'
                : `${record.durationInMonths}ヶ月間`}
          </Text>
        </Space>
      ),
    },
    {
      title: '発行状況',
      key: 'issuanceStatus',
      width: 150,
      render: (_, record) => {
        const limit =
          record.issueLimit === 'unlimited'
            ? '無制限'
            : record.issueLimit === 'once_per_agency'
              ? '1回のみ'
              : `${record.maxIssuesPerAgency}回まで`;

        return (
          <Space direction="vertical" size={0}>
            <Text>
              {record.myIssuedCount} / {limit}
            </Text>
            {record.canIssue ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                発行可能
              </Tag>
            ) : (
              <Tag color="default" icon={<StopOutlined />}>
                発行済み
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '発行済みコード',
      key: 'issuedCodes',
      render: (_, record) => {
        if (record.myIssuances.length === 0) {
          return <Text type="secondary">未発行</Text>;
        }

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {record.myIssuances.map((issuance) => (
              <div key={issuance.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyCode(issuance.promotionCode)}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    <Text code strong>
                      {issuance.promotionCode}
                    </Text>
                  </Button>
                  <Switch
                    size="small"
                    checked={issuance.isActive}
                    loading={toggleStatusMutation.isPending && pendingIssuanceId === issuance.id}
                    onChange={(checked) => handleToggleStatus(issuance.id, checked)}
                    checkedChildren="有効"
                    unCheckedChildren="無効"
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  <Space size="small">
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      使用: {issuance.timesRedeemed} / {issuance.maxRedemptions}回
                    </Text>
                  </Space>
                </div>
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => {
        if (!record.canIssue) {
          return (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.canIssueReason || '発行済み'}
            </Text>
          );
        }

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Popconfirm
              title="プロモーションコードを発行しますか？"
              description="発行したコードは取り消せません。"
              onConfirm={() => handleIssue(record.id)}
              okText="発行"
              cancelText="キャンセル"
            >
              <Button
                type="primary"
                icon={<GiftOutlined />}
                loading={issueMutation.isPending}
                block
              >
                コード発行
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const coupons = (couponsData?.coupons ?? []).map<CouponRow>((coupon) => ({
    id: coupon.id,
    stripeCouponId: coupon.stripeCouponId,
    stripeCouponName: coupon.stripeCouponName,
    displayName: coupon.displayName,
    discountType: coupon.discountType,
    discountValue: coupon.discountValue,
    currency: coupon.currency,
    duration: coupon.duration,
    durationInMonths: coupon.durationInMonths,
    issueLimit: coupon.issueLimit,
    maxIssuesPerAgency: coupon.maxIssuesPerAgency,
    canIssue: coupon.canIssue,
    canIssueReason: coupon.canIssueReason,
    myIssuedCount: coupon.myIssuedCount,
    myIssuances: coupon.myIssuances.map((issuance) => ({
      id: issuance.id,
      promotionCode: issuance.promotionCode,
      issuedAt: normalizeDateText(issuance.issuedAt),
      isActive: issuance.isActive,
      timesRedeemed: issuance.timesRedeemed,
      maxRedemptions: issuance.maxRedemptions,
    })),
  }));

  return (
    <PageContainer>
      <PageHeader
        title="クーポン管理"
        subtitle="管理者から割り当てられたクーポンからプロモーションコードを発行できます。発行したコードは顧客に提供することで割引を適用できます。"
      />

      <Alert
        message="グローバルクーポンシステム"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            管理者が登録したグローバルクーポンは、全ての代理店が利用可能です。
            各クーポンには発行制限が設定されており、制限に達するまで発行できます。
            発行したプロモーションコードは99999回まで使用可能です。
          </Paragraph>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 統計情報 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="発行総数"
              value={stats?.totalIssuances || 0}
              prefix={<GiftOutlined />}
              suffix="回"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="手動発行"
              value={stats?.byIssuedVia?.manual || 0}
              suffix="回"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="自動発行"
              value={stats?.byIssuedVia?.auto_user_creation || 0}
              suffix="回"
            />
          </Card>
        </Col>
      </Row>

      {/* クーポン一覧 */}
      <Card title="利用可能なクーポン一覧">
        <Table
          columns={columns}
          dataSource={coupons}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </PageContainer>
  );
}

export default AgencyCouponsPage;
